/**
 * ZEN ERP — Dashboard Sipariş Geçmişi Tablosu
 * DashboardPage.jsx'ten çıkarıldı.
 */
import React from "react";
import { X, Search } from "lucide-react";
import { fmt, fmtDate, ono } from "../../appUtils";
import { STATUS_LABELS } from "../../mock";

export function DashboardOrderHistory({
  summary,
  tr,
  lang,
  dateLocale,
  historyStatusFilter,
  setHistoryStatusFilter,
  historyCustomerQ,
  setHistoryCustomerQ,
  setViewingOrder,
}) {
  const orders = summary.allActiveOrders || [];
  if (orders.length === 0) return null;

  const todayStr = new Date().toISOString().slice(0, 10);
  const oneWeekAgoStr = (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); })();

  const statusOptions = [
    { value: "all",          label: tr("Tümü") },
    { value: "imalata_hazir", label: tr("Onaylandı / İmalata Hazır") },
    { value: "teklif",        label: tr("Teklif") },
    { value: "onay_bekliyor", label: tr("Onay Bekliyor") },
    { value: "imalatta",      label: tr("İmalatta") },
    { value: "kargolanacak",  label: tr("Kargolanacak") },
    { value: "yollandi",      label: tr("Yollandı") },
  ];

  const filtered = orders.filter((o) => {
    const statusMatch = historyStatusFilter === "all" || o.status === historyStatusFilter;
    const customerMatch = !historyCustomerQ || (o.customerName || "").toLowerCase().includes(historyCustomerQ.toLowerCase()) || (o.projectName || "").toLowerCase().includes(historyCustomerQ.toLowerCase());
    return statusMatch && customerMatch;
  });

  const chipColors = {
    sky:     "bg-sky-500/15 text-sky-700 dark:text-sky-300",
    amber:   "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    orange:  "bg-orange-500/15 text-orange-700 dark:text-orange-300",
    emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    violet:  "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    slate:   "bg-slate-500/15 text-slate-600 dark:text-slate-400",
  };

  return (
    <div className="zen-anim-up">
      {/* Başlık + filtreler */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2 mr-auto">
          <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />
          {tr("Sipariş Geçmişi")}
          <span className="text-[11px] font-normal text-slate-400">{filtered.length}/{orders.length} {tr("kayıt")}</span>
        </h3>

        {/* Müşteri/Proje arama */}
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white dark:bg-[#131B2E] border border-slate-200 dark:border-[#22304A] text-[12px]">
          <Search className="w-3 h-3 text-slate-400 shrink-0" />
          <input
            data-testid="history-customer-filter"
            value={historyCustomerQ}
            onChange={(e) => setHistoryCustomerQ(e.target.value)}
            placeholder="Müşteri / proje ara…"
            className="bg-transparent outline-none w-36 placeholder-slate-400 dark:placeholder-slate-600 text-slate-800 dark:text-slate-100"
          />
          {historyCustomerQ && <button onClick={() => setHistoryCustomerQ("")}><X className="w-3 h-3 text-slate-400 hover:text-slate-600" /></button>}
        </div>

        {/* Durum pill filtreleri */}
        <div className="flex items-center gap-1 flex-wrap">
          {statusOptions.map(({ value, label }) => (
            <button
              key={value}
              data-testid={`history-filter-${value}`}
              onClick={() => setHistoryStatusFilter(value)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors border ${historyStatusFilter === value ? "bg-indigo-600 text-white border-indigo-600 shadow-sm" : "bg-white dark:bg-[#131B2E] border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-500"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tablo */}
      <div className="zen-panel !p-0 overflow-hidden overflow-x-auto">
        <table className="zen-table zen-table--sm" style={{ minWidth: 680 }}>
          <thead>
            <tr>
              <th>{tr("Sipariş No")}</th>
              <th>{tr("Müşteri / Proje")}</th>
              <th className="text-center">{tr("Durum")}</th>
              <th className="text-center">{tr("Oluşturma")}</th>
              <th className="text-center">{tr("Onay Tarihi")}</th>
              <th className="text-center">{tr("Termin")}</th>
              <th className="text-right">{tr("Tutar")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-[12px]">{tr("Filtrelerle eşleşen sipariş yok")}</td></tr>
            )}
            {filtered.map((o) => {
              const isOverdue = o.deadline
                ? o.deadline < todayStr
                : (o.createdAt && o.createdAt.slice(0, 10) <= oneWeekAgoStr && !["odendi", "iptal"].includes(o.status));
              const stat = STATUS_LABELS[o.status] || { tr: o.status, color: "slate" };
              const approvalEntry = (o.statusHistory || []).find(h => h.status === "imalatta");
              const approvalDate = approvalEntry?.at
                ? new Date(approvalEntry.at).toLocaleDateString(dateLocale, { day: "2-digit", month: "2-digit", year: "2-digit" })
                : null;
              const createdDate = o.createdAt
                ? new Date(o.createdAt).toLocaleDateString(dateLocale, { day: "2-digit", month: "2-digit", year: "2-digit" })
                : "—";
              return (
                <tr
                  key={o.id}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-[#1A2340] transition-colors"
                  onClick={() => setViewingOrder(o)}
                  data-testid={`history-row-${o.id}`}
                >
                  <td>
                    <span className="zen-mono font-black text-[#E5322A]">{ono(o)}</span>
                    {isOverdue && (
                      <span
                        className="zen-blink inline-block ml-1.5 text-rose-500 font-black leading-none align-middle"
                        title={o.deadline ? tr("Termin süresi geçti") : tr("1 haftayı geçti, termin girilmemiş")}
                        style={{ fontSize: "1em" }}
                      >!</span>
                    )}
                  </td>
                  <td>
                    <div className="font-semibold text-[12px] truncate max-w-[180px]">{o.customerName}</div>
                    {o.projectName && <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{o.projectName}</div>}
                  </td>
                  <td className="text-center">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${chipColors[stat.color] || chipColors.slate}`}>
                      {stat.tr}
                    </span>
                  </td>
                  <td className="text-center zen-mono text-[11px] text-slate-500">{createdDate}</td>
                  <td className="text-center zen-mono text-[11px] text-slate-500">{approvalDate || <span className="text-slate-300 dark:text-slate-600">—</span>}</td>
                  <td className="text-center">
                    {o.deadline ? (
                      <span className={`zen-mono text-[11px] font-semibold ${isOverdue ? "text-rose-500" : "text-slate-600 dark:text-slate-400"}`}>
                        {fmtDate(o.deadline)}
                      </span>
                    ) : (
                      <span className={isOverdue ? "zen-mono text-[11px] font-semibold text-amber-500" : "text-slate-300 dark:text-slate-600"}>
                        {isOverdue ? "girilmemiş" : "—"}
                      </span>
                    )}
                  </td>
                  <td className="text-right zen-mono font-bold text-[12px]">{fmt(o.total, o.currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
